import React from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import { IconButton } from '@mui/material';
import { Command, tabActions } from '@store/slices';
import { useDispatch } from 'react-redux';
import { AddOutlined, DeleteOutlineOutlined } from '@mui/icons-material';
import { Button } from '@common/Button';
import { Label } from '@common/Label';
import { useTheme } from '../../../../styles/theme';
import { NodeField, NodeInput, NodeInputType, NodeRequestParameter } from '@components/Flow/Nodes';
import { Expression } from '@common/Expression';
import { useNodeData } from '@common/hooks/useNodeData';
import { BasicInput } from '@common/Expression/Basic';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 8px;

  & .MuiInputAdornment-root {
    margin-right: 10px;
  }
`;

const ParameterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1 1 auto;
  width: 100%;
  max-width: calc((100vw - 60px) * (2.5 / (7.5 + 2.5)));
`;

const Parameter = styled.div`
  display: grid;
  grid-gap: 8px;
  grid-template-columns: 3fr 7fr 42px;
`;

const Action = styled.div`
  display: flex;
  align-items: center;

  svg {
    font-size: 2ch;
  }
`;

type StateOverride = {
  value: NodeRequestParameter[];
  addField: () => void;
  title: string;
  tooltip: string;
  updateField: (nestedFieldId: string, fieldName: string, newValue: string) => void;
  deleteField: (nestedFieldId: string) => void;
  allowToolArgs?: boolean;
};

interface Props {
  command: Command;
  field: NodeField;
  stateOverride?: StateOverride;
}

export const RequestParametersExpression: React.FC<Props> = ({ command, field, stateOverride }) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const { state } = command;
  const { id, label, description } = field;
  const { getOptions, dataEdges } = useNodeData(command?.node?.id);

  const dataOptions = getOptions(command?.node?.id, stateOverride?.allowToolArgs);
  const value = stateOverride?.value || (state[id] as NodeRequestParameter[]);

  const addField = () => {
    if (stateOverride?.addField) return stateOverride.addField();

    const inputValue: NodeInput<string> = { type: NodeInputType.Expression, value: '' };
    const value = { id: uuidv4(), name: '', value: inputValue };

    const input = {
      nodeId: command.node.id,
      fieldId: id,
      value: value,
    };

    dispatch(tabActions.nodeInputArrayPush(input));
    dispatch(tabActions.setSaved(false));
  };

  const updateField = (nestedFieldId: string, fieldName: string, newValue: string) => {
    if (stateOverride?.updateField) return stateOverride.updateField(nestedFieldId, fieldName, newValue);

    const input = {
      nodeId: command.node.id,
      fieldId: id,
      nestedFieldId,
      fieldName,
      value: fieldName === 'value' ? { type: NodeInputType.Expression, value: newValue } : newValue,
    };

    dispatch(tabActions.nodeInputArrayUpdate(input));
    dispatch(tabActions.setSaved(false));
  };

  const deleteField = (nestedFieldId: string) => {
    if (stateOverride?.deleteField) return stateOverride.deleteField(nestedFieldId);

    dispatch(
      tabActions.nodeInputArrayFilter({
        nodeId: command.node.id,
        fieldId: id,
        nestedFieldId,
      })
    );

    dispatch(tabActions.setSaved(false));
  };

  const totalAvailableWidth = (window.innerWidth - 60) * (2.5 / (7.5 + 2.5)) - 90;
  const basicInputMaxWidth = totalAvailableWidth * (4.2 / 10);
  const expressionMaxWidth = totalAvailableWidth * (5.8 / 10);

  if (!field || !command) return null;

  return (
    <Container>
      <Label
        title={stateOverride?.title || label}
        tooltip={stateOverride?.tooltip || description}
        onClick={value?.length !== 0 && value ? addField : undefined}
      />
      <ParameterContainer>
        {(value || [])?.map(({ id, name, value }) => (
          <Parameter key={`${id}-${command.node.id}-${field.id}-field`}>
            <BasicInput
              key={`${id}-${command.node.id}-${field.id}-name`}
              keyProp={`${id}-${command.node.id}-${field.id}`}
              value={name}
              setValue={(value) => updateField(id, 'name', value)}
              placeholder='param'
              minRows={1}
              maxRows={1}
              wrapping={false}
              maxWidth={`${basicInputMaxWidth}px`}
            />
            {value?.type === NodeInputType.Expression ? (
              <Expression
                key={`${id}-${command.node.id}-${field.id}-value`}
                keyProp={`${id}-${command.node.id}-${field.id}`}
                value={value.value}
                dataEdges={dataEdges}
                wrapping={false}
                title={stateOverride?.title || label}
                description={stateOverride?.tooltip || description}
                setValue={(value) => updateField(id, 'value', value)}
                options={dataOptions}
                placeholder='value'
                minRows={1}
                maxRows={1}
                maxWidth={`${expressionMaxWidth}px`}
              />
            ) : (
              <BasicInput
                key={`${id}-${command.node.id}-${field.id}-value`}
                keyProp={`${id}-${command.node.id}-${field.id}`}
                value={value.value}
                setValue={(value) => updateField(id, 'value', value)}
                wrapping={false}
                placeholder='value'
                maxWidth={`${expressionMaxWidth}px`}
              />
            )}
            <Action>
              <IconButton
                sx={{ border: `1px solid ${theme.line.light}`, height: '42px', width: '42px' }}
                onClick={() => deleteField(id)}
              >
                <DeleteOutlineOutlined sx={{ fontSize: '1.4ch !important', color: theme.text.subtext }} />
              </IconButton>
            </Action>
          </Parameter>
        ))}
        {(value?.length === 0 || !value) && (
          <Button color='primary' fullWidth onClick={addField}>
            <AddOutlined sx={{ fontSize: '14px', color: theme.text.standard }} />
          </Button>
        )}
      </ParameterContainer>
    </Container>
  );
};
